import {
  Box, Button, Grid, Link,
} from '@mui/material';
import {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import BlockComponentButton, { ButtonSizeMap } from '../../models/BlockComponentValues/BlockComponentButton';
import BlockData from '../../models/BlockData';
import { URLOpenType } from '../../models/URLOpenType';
import { URLTargetType } from '../../models/URLTargetType';
import LinkModal from '../Modals/LinkModal';
import { ColorPickerColorsMap } from '../../models/ColorPicker';
import { FileContext } from '../../contexts/FileContext';

interface LinkButtonProps {
  button: BlockComponentButton;
  isEditable: boolean;
  isTemplate?: boolean;
  blockData: BlockData[];
  updateHandler: (item: any) => void;
  mainColor?: string;
  fontFamily?: string;
}

function LinkButton({
  isEditable,
  isTemplate,
  button,
  blockData,
  updateHandler,
  mainColor,
  fontFamily,
}: LinkButtonProps) {
  const { getDownloaddableFileUrl } = useContext(FileContext);
  const [openLinkModal, setOpenLinkModal] = useState(false);
  const [item, setItem] = useState(button);
  const [error, setError] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [buttonTextColor] = useState(ColorPickerColorsMap[button.settings.colorText] || button.settings.colorText || '#ffffff');
  const [buttonBorderColor, setBorderColor] = useState(ColorPickerColorsMap[button.settings.border] || button.settings.border || '#ffffff');

  const hexToRgb = (hex: string) => {
    const match = /^#([A-Fa-f0-9]{6})$/.exec(hex);
    if (!match) return '0, 188, 212';
    const [, color] = match;
    return `${parseInt(color.slice(0, 2), 16)}, ${parseInt(color.slice(2, 4), 16)}, ${parseInt(color.slice(4, 6), 16)}`;
  };

  const buttonStyle = useMemo(() => {
    const baseColor = ColorPickerColorsMap[button.settings.color] === '#2664b1' && mainColor
      ? mainColor
      : ColorPickerColorsMap[button.settings.color] || button.settings.color;

    const convertedButtonColor = hexToRgb(baseColor);

    let opacity;
    if (baseColor === 'transparent' || button.settings.color === 'transparent') {
      opacity = 0;
    } else if (isHovered) {
      opacity = 0.8;
    } else {
      opacity = 1;
    }

    return {
      minWidth: ButtonSizeMap[button.settings.size],
      backgroundColor: `rgba(${convertedButtonColor}, ${opacity})`,
      color: buttonTextColor,
      border: `2px solid ${buttonBorderColor}`,
      fontFamily,
      padding: '1.05rem',
    };
  }, [button.settings.color, button.settings.size, mainColor, isHovered, buttonTextColor, buttonBorderColor, fontFamily]);

  const handleOpenLinkModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenLinkModal(true);
    event.stopPropagation();
  };

  const handleCloseLinkModal = (event: React.MouseEvent) => {
    setError('');
    setItem(button);
    setOpenLinkModal(false);
    event.stopPropagation();
  };

  const handleSaveLinkModal = () => {
    if (!item.link.title) {
      setError('Please enter a title');
    } else {
      setError('');
      updateHandler(item);
      setOpenLinkModal(false);
    }
  };

  useEffect(() => {
    setBorderColor(ColorPickerColorsMap[button.settings.border] || button.settings.border || '#ffffff');
  }, [button.settings.border]);

  if (!isEditable && !isTemplate) {
    return (
      <Box>
        <Grid
          container
          justifyContent={button.settings.justify || 'center'}
          alignItems="center"
          sx={{ padding: 1 }}
        >
          <Link
            sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
            href={button.link.linkData.url}
            target={button.link.linkData.openType}
            rel="noreferrer"
            underline="none"
          >
            <Button
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className="Button-More"
              style={{ ...buttonStyle, textTransform: 'none' }}
              size={button.settings.size}
            >
              <div translate="no" className="notranslate" dangerouslySetInnerHTML={{ __html: button.link.title }} />
            </Button>
          </Link>
        </Grid>
      </Box>
    );
  }

  if (!isEditable && isTemplate) {
    return (
      <Box>
        <Grid
          container
          justifyContent={button.settings.justify || 'center'}
          sx={{
            padding: 1,
          }}
        >
          <Button
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="Button-More"
            style={{ ...buttonStyle, textTransform: 'none' }}
            size={button.settings.size}
          >
            <div translate="no" className="notranslate" dangerouslySetInnerHTML={{ __html: button.link.title }} />
          </Button>
        </Grid>
      </Box>
    );
  }

  return (
    <Box onClick={(event) => event.stopPropagation()}>
      <Grid
        container
        justifyContent={button.settings.justify || 'center'}
        sx={{
          padding: 1,
        }}
      >
        <Button
          className="Button-More"
          onClick={(event) => handleOpenLinkModal(event)}
          style={{ ...buttonStyle, textTransform: 'none' }}
          size={button.settings.size}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div translate="no" className="notranslate" dangerouslySetInnerHTML={{ __html: button.link.title }} />
        </Button>
      </Grid>

      <LinkModal
        openModal={openLinkModal}
        onCloseModal={(event) => handleCloseLinkModal(event)}
        onSaveModal={handleSaveLinkModal}
        link={item.link}
        errorText={error}
        blockData={blockData}
        initialTextColor={buttonTextColor}
        hideRemoveButton
        onChangeURLTitle={(newTitle) => { setItem({ ...item, link: { ...item.link, title: newTitle } }); }}
        onChangeTargetType={(newTargetType) => {
          if (newTargetType === URLTargetType.INTERNAL) {
            setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, targetType: newTargetType, openType: URLOpenType.SAME_PAGE } } });
          } else {
            setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, targetType: newTargetType, url: '' } } });
          }
        }}
        onSelectBlock={(selected) => { setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, url: selected } } }); }}
        onChangeLink={(newLink) => { setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, url: newLink } } }); }}
        onChangeFile={(newFile) => { setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, url: getDownloaddableFileUrl(newFile?.file_name) } } }); }}
        onChangeOpenType={(checked) => { setItem({ ...item, link: { ...item.link, linkData: { ...item.link.linkData, openType: checked ? URLOpenType.NEW_TAB : URLOpenType.SAME_PAGE } } }); }}
        onChangeSize={(newSize) => { setItem({ ...item, settings: { ...item.settings, size: newSize } }); }}
        onChangeColor={(newColor) => { setItem({ ...item, settings: { ...item.settings, color: newColor } }); }}
        onChangeBorderColor={(newBorderColor) => { setItem({ ...item, settings: { ...item.settings, border: newBorderColor } }); }}
        settings={item.settings}
      />
    </Box>
  );
}

export default LinkButton;
